/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable complexity */
import type { Maybe, OptionalPromise } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { Materialize } from '@adornis/materialized-views/decorators/materialize.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { Subject, bufferTime, filter, map, merge, mergeMap, of, share, switchMap, type Observable } from 'rxjs';
import { bhBaseQlContext } from '../bh-context.js';
import { TimeSlot } from '../datetime/time-slot.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { notifyNuzterSperrungAufgehoben } from '../notification/nutzer-sperrung-aufgehoben.js';
import { notifyNuzterSperrung } from '../notification/nutzer-sperrung.js';
import { notifyVeranstaltungseintragungDurchAdmin } from '../notification/veranstaltungseintragung-durch-admin.js';
import { getPluginForScope } from '../plugins/plugin.js';
import { getMailer } from '../util.js';
import { BHPayment } from './BHPayment.js';
import { MailTemplateVars } from './mail-templates.js';
import { Product } from './products.js';
import { UserStammdaten } from './stammdaten.js';
import { User } from './user.js';

// create indices
(async () => {
  if (!context?.serverContext) return;
  const participationCollection = await getRawCollection('participations');
  await participationCollection.createIndex({ userID: 1 });
  await participationCollection.createIndex({ productID: 1 });
  await participationCollection.createIndex({ state: 1 });
  await participationCollection.createIndex({ userID: 1, productID: 1 }, { unique: true });
  await participationCollection.createIndex(
    { productID: 1, waitingListPlace: 1 },
    {
      unique: true,
      partialFilterExpression: {
        waitingListPlace: { $type: 'number' },
      },
    },
  );
})();

@Entity()
export class SimplifiedPrice extends AdornisEntity {
  static override _class = 'SimplifiedPrice';

  @Field(type => Int)
  unitAmount!: number;

  @Field(type => String)
  currency!: string;
}

@Materialize({
  selectionSet: async () => {
    const { Suspension } = await import('./suspension.js');
    const { UserStammdaten } = await import('./stammdaten.js');
    const { Appointment } = await import('./appointment.js');
    const { SimplifiedPrice } = await import('./Participation.js');

    return {
      userID: 1,
      user: {
        email: 1,
        username: 1,
        title: 1,
        firstName: 1,
        lastName: 1,
        isLdapAccount: 1,
        stripeCustomerID: 1,
        suspentions: selectionSet(() => Suspension, 1),
        whatsappNotification: 1,
        whatsappPhone: 1,
        receivesNewsletter: 1,
      },
      userStammdaten: selectionSet(() => UserStammdaten, 3),
      productID: 1,
      product: {
        name: 1,
        prices: { unitAmount: 1 },
        sortingWeight: 1,
        hasPrice: 1,
        category: { name: 1 },
        institutionID: 1,
        timeframeID: 1,
        timeframe: { _id: 1, name: 1 },
        productCopyID: 1,
        isLDAPOnly: 1,
        publishDate: 1,
        topic: 1,
        language: 1,
        presenceType: 1,
        isCanceled: 1,
        capacity: 1,
        appointments: selectionSet(() => Appointment, 1),
      },
      createdAt: 1,
      updatedAt: 1,
      currentPayment: {
        status: 1,
        participationIDs: 1,
        orderNr: { year: 1, nr: 1 },
        discount: 1,
        amount: 1,
      },
      state: 1,
      waitingListPlace: 1,
      waitingListPriority: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      isNoShow: 1,
      productPriceForUser: selectionSet(() => SimplifiedPrice, 1),
      fulfilledParticipationRequirements: 1,
      registrationDate: 1,
      signoffDate: 1,
      nachrueckPriority: 1,
      appointmentParticipations: { _id: 1, appointmentID: 1, date: 1 },
      registrationDateThroughAdmin: 1,
      signoffDateThroughAdmin: 1,
      ikh_doNotSendInvoice: 1,
    };
  },
})
@Entity()
export class Participation<Tproduct extends Product = Product> extends MongoEntity {
  public static override _class = 'Participation';
  public static override _collectionName = 'participations';

  @validate(nonOptional())
  @Field(type => ID)
  userID!: string;

  @DataLoadedField(type => User, participation => ({ _id: participation.userID }))
  user: Maybe<User>;

  @Field(type => UserStammdaten, {
    resolve: function (this: Participation) {
      return async (gqlFields: BaseQLSelectionSet<UserStammdaten>) => {
        // @ts-expect-error loadMongoData return-type is Array but should only be entity
        const participation = (await loadMongoData(Participation, this._id, {
          projection: { userID: 1, productID: 1 },
        })) as Participation;
        if (!participation) throw new Error('participation not found ' + this._id);

        const product = await getByID(Product, participation.productID)({ institutionID: 1 });
        if (!product) throw new Error('product not found ' + this.productID);

        const stammdaten = await UserStammdaten.getExistingStammdaten(
          participation.userID,
          product.institutionID,
          null,
        )(gqlFields);
        return stammdaten;
      };
    },
  })
  userStammdaten: Maybe<UserStammdaten>;

  @validate(nonOptional())
  @Field(type => ID)
  productID!: string;

  @DataLoadedField(type => Product, participation => ({ _id: participation.productID }))
  product: Maybe<Tproduct>;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  createdAt!: DateTime;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  updatedAt!: DateTime;

  @Field(type => BHPayment, {
    resolve: function (this: Participation) {
      return async () => {
        const paymentCollection = await getCollection(BHPayment);
        const payments = await paymentCollection.find({ participationIDs: this._id }).sort({ createdAt: -1 }).toArray();
        return payments.at(0) as Maybe<BHPayment>; // return latest payment
      };
    },
  })
  currentPayment: Maybe<BHPayment>;

  @validate(options => {
    if (!options.value) throw new ValidationError('state must be set', { key: options.key, instance: options.target });
  })
  @Field(type => String, { default: value => value ?? ParticipationState.IN })
  state!: ParticipationState;

  @Field(type => Boolean, { default: value => value ?? false })
  lastStateChangeWasInitiatedByAdmin: Maybe<Boolean>;

  @Field(type => Int)
  waitingListPlace: Maybe<number>;

  @Field(type => String)
  nachrueckPriority: Maybe<'high' | 'low'>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(type => Boolean, {
    resolve: function (this: Participation) {
      return () => {
        const isIn = this.state === ParticipationState.IN;
        return isIn && this.state !== ParticipationState.PARTICIPATED;
      };
    },
  })
  isNoShow: Maybe<boolean>;

  @Field(type => SimplifiedPrice, {
    resolve: function (this: Participation) {
      return async () => {
        const product = await getByID(Product, this.productID)({ hasPrice: 1, institutionID: 1 });
        if (!product) throw new Error('product not found');

        if (!product.hasPrice) return null;
        const paymentPlugin = getPluginForScope('payment', product.institutionID);
        const price = await paymentPlugin.getProductPrice(this.productID, this.userID);
        if (!price) throw new Error('price not found');

        return new SimplifiedPrice({
          unitAmount: price.unitAmount,
          currency: price.currency,
        });
      };
    },
  })
  productPriceForUser: Maybe<SimplifiedPrice>;

  @Field(type => [String], { default: v => v ?? [] })
  fulfilledParticipationRequirements: Maybe<string[]>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(type => DateTime, {
    resolve: function (this: Participation) {
      return async () => {
        if (this.lastStateChangeWasInitiatedByAdmin) return null;
        return this.createdAt;
      };
    },
  })
  registrationDate: Maybe<DateTime>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(type => DateTime, {
    resolve: function (this: Participation) {
      return async () => {
        if (this.state === ParticipationState.LEFT || this.lastStateChangeWasInitiatedByAdmin) return null;
        return this.updatedAt;
      };
    },
  })
  signoffDate: Maybe<DateTime>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(type => DateTime, {
    resolve: function (this: Participation) {
      return async () => {
        if (this.lastStateChangeWasInitiatedByAdmin) return this.createdAt;
        return null;
      };
    },
  })
  registrationDateThroughAdmin: Maybe<DateTime>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(type => DateTime, {
    resolve: function (this: Participation) {
      return async () => {
        if (this.state === ParticipationState.LEFT && this.lastStateChangeWasInitiatedByAdmin) return this.updatedAt;
        return null;
      };
    },
  })
  signoffDateThroughAdmin: Maybe<DateTime>;

  @Field(type => Boolean)
  ikh_doNotSendInvoice?: boolean;

  static override async transformBeforeUpdate(oldEntity: PartialEntityData<Participation>, update: Participation) {
    update.lastStateChangeWasInitiatedByAdmin = !!bhBaseQlContext.participationStateChangeWasInitiatedByAdmin;
    update.updatedAt = DateTime.now();

    super.transformBeforeUpdate(oldEntity, update);
  }

  public async timespan(): Promise<TimeSlot[]> {
    throw new Error('this method should be overridden');
  }

  public async getPriceForUser() {
    const product = await getByID(Product, this.productID)({ institutionID: 1 });
    if (!product) throw new Error('product not found ' + this.productID);
    const paymentPlugin = getPluginForScope('payment', product.institutionID);
    return paymentPlugin.getProductPrice(this.productID, this.userID);
  }

  public failWhenSuspended() {
    if (
      this.state === ParticipationState.SUSPENDED &&
      this.user?.suspentions.map(suspention => suspention.institutionID).includes(this.product?.institutionID ?? '')
    )
      throw new Error('Sie sind suspendiert!');
  }

  override toString() {
    return `${this.user?.email} -> ${this.product?.name ?? '<Titel>'} (${this.state})`;
  }
  static async suspendParticipationsByUserInstitution(userID: string, institutionID: string) {
    const collection = await getRawCollection<Participation>(Participation._collectionName);
    const { Product } = await import('./products.js');
    const result = await collection
      .aggregate<Participation>([
        {
          $match: {
            userID,
            state: { $ne: ParticipationState.PARTICIPATED },
          },
        },
        {
          $lookup: {
            from: Product._collectionName,
            localField: 'productID',
            foreignField: '_id',
            as: 'product',
          },
        },
        {
          $unwind: '$product',
        },
        {
          $match: {
            'product.institutionID': institutionID,
          },
        },
      ])
      .toArray();

    const user = await getByID(
      User,
      userID,
    )({ title: 1, firstName: 1, lastName: 1, email: 1, whatsappNotification: 1, whatsappPhone: 1 });
    if (!user) return;

    // TODO: move nachrueck logic to its own package or this function into buchungsheld entrypoint to avoid circular dependencies
    const m = await import('../../../buchungsheld/src/server/waitinglist-monitoring.js');
    for (const participation of result) {
      const constructedParticipation = constructValue(participation) as Participation;
      constructedParticipation.state = ParticipationState.SUSPENDED;
      await constructedParticipation.save();
      await notifyNuzterSperrung(participation._id);
      await m.nachrueckLogic(participation.productID);
    }
  }

  @Query(type => [Participation])
  static getParticipationsByProductIDsAndState(
    @Arg('productIDs', type => [String]) productIDs: string[],
    @Arg('states', type => [String])
    states: ParticipationState[] = [ParticipationState.IN, ParticipationState.WAITING],
  ) {
    return async (gqlFields: BaseQLSelectionSet<Participation>) => {
      const collection = await getCollection<Participation>(Participation._class);
      const result = await collection
        .find<Participation>({
          productID: {
            $in: productIDs,
          },
          state: { $in: states },
        })
        .toArray();
      return result;
    };
  }

  @Query(type => Int)
  static cancelEnrollment(
    @Arg('participationID', type => String) participationID: string,
    @Arg('throughAdmin', type => Boolean) throughAdmin: boolean,
  ) {
    return async () => {
      const _user = await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
      if (throughAdmin && !(_user as User).isAdmin) throw new Error('action not permitted');

      // check if cancel possible
      const participation = await getByID(
        Participation,
        participationID,
      )({
        _id: 1,
        productID: 1,
        state: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        timeslotID: 1,
        waitingListPlace: 1,
        nachrueckPriority: 1,
        product: {
          _id: 1,
          institutionID: 1,
          settings: { blockSignOffDaysInAdvance: 1, inheritedBlockSignOffDaysInAdvance: 1 },
        },
      });
      if (!participation) throw new Error('participation is not defined');
      const product = participation.product;
      if (!product) throw new Error('product is not defined');

      const m = await import('../api/participation.js');
      return m._cancelEnrollment(participation, throughAdmin);
    };
  }

  static async cancelSuspensionParticipationsByUserInstitution(userID: string, institutionID: string) {
    const collection = await getRawCollection<Participation>(Participation._collectionName);
    const { Product } = await import('./products.js');
    const result = await collection
      .aggregate<Participation>([
        {
          $match: {
            userID,
            state: ParticipationState.SUSPENDED,
          },
        },
        {
          $lookup: {
            from: Product._collectionName,
            localField: 'productID',
            foreignField: '_id',
            as: 'product',
          },
        },
        {
          $unwind: '$product',
        },
        {
          $match: {
            'product.institutionID': institutionID,
          },
        },
      ])
      .toArray();

    const user = await getByID(
      User,
      userID,
    )({ title: 1, firstName: 1, lastName: 1, email: 1, whatsappNotification: 1, whatsappPhone: 1 });
    if (!user) return;

    for (const participation of result) {
      const constructedParticipation = constructValue(participation) as Participation;
      constructedParticipation.state = ParticipationState.LEFT;
      await constructedParticipation.save();
      await notifyNuzterSperrungAufgehoben(participation._id);
    }
  }

  @Query(type => Int)
  static countMatchingParticipations(
    @Arg('participated', type => Boolean) participated?: boolean,
    @Arg('institutionID', type => String) institutionID?: string,
    @Arg('timeframeID', type => String) timeframeID?: string,
  ) {
    return async () => {
      const collection = await getRawCollection<Participation>(this._collectionName);

      const result = await collection
        .aggregate([
          {
            $match: {
              ...(typeof participated === 'boolean'
                ? { state: participated ? ParticipationState.PARTICIPATED : ParticipationState.IN }
                : {}),
            },
          },
          {
            $lookup: {
              from: 'products',
              localField: 'productID',
              foreignField: '_id',
              as: 'product',
            },
          },
          {
            $match: {
              ...(institutionID ? { 'product.institutionID': institutionID } : {}),
              ...(timeframeID ? { 'product.timeframeID': timeframeID } : {}),
            },
          },
          {
            $count: 'total',
          },
        ])
        .toArray();

      return result[0]?.total ?? 0;
    };
  }

  static sendMailEvent = (() => {
    const sub = new Subject<EmailEvent>();
    const obs = sub.pipe(
      bufferTime(500),
      // group mail-events of the same emailType+email and merge the 'changed_registrations'
      mergeMap(events => {
        const groups: Record<string, EmailEvent[]> = {};
        for (const e of events) {
          const [email, tmplate] = e;
          const key = tmplate.emailType + '_' + email.to.toString();
          if (!groups[key]) groups[key] = groups[key] = [];
          groups[key]!.push(e);
        }

        const newEventsObservables = [] as Array<Observable<EmailEvent>>;
        for (const events of Object.values(groups)) {
          const firstEvent = events.shift()!;
          for (const e of events) {
            const otherChangedRegistrations = e[2][MailTemplateVars.CHANGED_REGISTRATIONS];
            firstEvent[2][MailTemplateVars.CHANGED_REGISTRATIONS].push(...otherChangedRegistrations);
            firstEvent[3].push(...e[3]);
          }
          newEventsObservables.push(of(firstEvent));
        }

        return merge(...newEventsObservables);
      }),
      filter(Boolean),
      mergeMap(async ([email, mailTemplate, vars, tickets]) => {
        const mailContent = await mailTemplate.buildEmail(vars);
        if (!mailContent) return { tickets, err: new Error("content couldn't be generated") };

        try {
          await getMailer().sendMail(
            await Message.compose({
              subject: mailTemplate.subject,
              html: mailContent,
            }),
            email,
          );
        } catch (err) {
          return { tickets, err };
        }

        return { tickets, err: null };
      }),
      share(),
    );

    return { sub, obs };
  })();

  static shouldSendParticipationMailAfterParticipationParticipatedGotUpdated(
    participation: NarrowedEntity<
      Participation,
      {
        state: 1;
        product: {
          settings: {
            autoSendCertifatesEnabled: 1;
          };
          attachedCertificate: {
            certificateID: 1;
          };
        };
      }
    >,
  ) {
    // always send not participatied mail
    if (participation.state !== ParticipationState.PARTICIPATED) {
      return { shouldSendMail: true, mail: 'nutzer-teilnahme-aufgehoben' as const };
    }

    const autoSendCertifatesEnabled = !!participation.product?.settings?.autoSendCertifatesEnabled;
    const certificateExists = !!participation.product?.attachedCertificate?.certificateID;

    if (autoSendCertifatesEnabled && certificateExists)
      return { shouldSendMail: true, mail: 'nutzer-teilnahme' as const };

    if (autoSendCertifatesEnabled && !certificateExists)
      return { shouldSendMail: true, mail: 'nutzer-teilnahme-without-certificate' as const };

    if (!autoSendCertifatesEnabled)
      return { shouldSendMail: true, mail: 'nutzer-teilnahme-without-certificate' as const };

    throw new Error('unreachable');
  }

  static async getAllowedParticipations() {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];

    const userIDs = await User.getAllowedUsers();

    const participationCol = await getRawCollection(Participation._collectionName);

    let result = await participationCol
      .aggregate<{ _id: string }>([
        {
          $match: {
            userID: {
              $in: userIDs,
            },
          },
        },
        {
          $project: {
            _id: 1,
          },
        },
      ])
      .toArray()
      .then(res => res.map(r => r._id));

    if (user.isLecturer) {
      const { Teaching } = await import('./teaching.js');
      const stuff = await participationCol
        .aggregate([
          { $lookup: { from: Product._collectionName, as: 'product', localField: 'productID', foreignField: '_id' } },
          { $unwind: '$product' },
          {
            $lookup: {
              from: Teaching._collectionName,
              as: 'teaching',
              localField: 'productID',
              foreignField: 'productID',
            },
          },
          { $unwind: '$teaching' },
          { $match: { 'teaching.lecturerID': user._id } },
          { $group: { _id: null, participationIDs: { $addToSet: '$_id' } } },
        ])
        .toArray();

      result = result.concat(stuff?.[0]?.participationIDs ?? []);
    }

    return result;
  }

  @Query(type => Int)
  static setIkhDoNotSendInvoice(
    @Arg('participationID', type => String) participationID: string,
    @Arg('doNotSendInvoice', type => Boolean) doNotSendInvoice: boolean,
  ) {
    return async () => {
      const c = await getCollection(Participation);
      await c.updateOne({ _id: participationID }, { $set: { ikh_doNotSendInvoice: doNotSendInvoice } });
      return 1;
    };
  }

  @Subscription(type => [TimeSlot])
  static getUserTimeSlotsOfActiveParticipations(@Arg('userID', type => String) userID: string) {
    return (gqlFields: BaseQLSelectionSet<TimeSlot>): Observable<TimeSlot[]> => {
      const participations$ = watchQuery(Participation._collectionName, {
        userID,
        state: { $in: [ParticipationState.PARTICIPATED, ParticipationState.IN] },
      });

      return participations$.pipe(
        switchMap(participationData => {
          return getByID(
            Participation,
            participationData?.map(pd => pd._id) ?? [],
          )({
            _id: 1,
            _class: 1,
            productID: 1,
            timeslotID: 1,
            product: {
              _id: 1,
              appointments: {
                _id: 1,
                start: 1,
                end: 1,
                isNonBlocking: 1,
                location: 1,
                name: 1,
                meetinLink: { link: 1 },
              },
              slots: { _id: 1, start: 1, end: 1, isNonBlocking: 1 },
            },
          });
        }),
        switchMap(participations => {
          return Promise.all(participations.map(p => p!.timespan()));
        }),
        map(deepSlots => deepSlots.flat()),
      );
    };
  }

  @Mutation(type => Boolean)
  static addUserManuallyToWorkshop(
    @Arg('userID', type => String) userID: string,
    @Arg('productID', type => String) productID: string,
    @Arg('participated', type => Boolean) participated: boolean,
    @Arg('timeslotID', type => String) timeslotID: Maybe<string>,
  ) {
    return async () => {
      const product = await getByID(Product, productID)({ _class: 1, institutionID: 1 });
      if (!product) throw new Error('product not found ' + productID);

      // check that user exists
      const user = await getByID(User, userID)({ _id: 1, _class: 1 });
      if (!user) throw new Error('user not found ' + userID);

      const currentUser = await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
      if (!currentUser || !currentUser.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_USERS)) {
        throw new Error('permission denied');
      }

      const participation = await (async () => {
        if (product._class === 'Workshop') {
          const m = await import('./ParticipationWorkshop.js');
          return new m.ParticipationWorkshop({
            userID: user._id,
            productID,
            state: participated ? ParticipationState.PARTICIPATED : ParticipationState.IN,
            lastStateChangeWasInitiatedByAdmin: true,
          });
        }

        if (product._class === 'Einzelberatung') {
          // necessary to avoid circular dependencies
          const m = await import('./ParticipationEinzelberatung.js');
          return new m.ParticipationEinzelberatung({
            userID: user._id,
            productID,
            state: participated ? ParticipationState.PARTICIPATED : ParticipationState.IN,
            lastStateChangeWasInitiatedByAdmin: true,
            timeslotID,
          });
        }

        throw new Error('unknown product class ' + product._class);
      })();

      bhBaseQlContext.participationStateChangeWasInitiatedByAdmin = true;
      await participation.create();
      await notifyVeranstaltungseintragungDurchAdmin(participation._id);
    };
  }
}

export type EmailEvent = [Recipient, MailTemplate, Record<string, any>, string[]];

@Entity()
export class ParticipationParticipatedFieldMappingEntry extends FieldMappingEntry<
  boolean,
  PartialEntityData<Participation>
> {
  static override _class = 'ParticipationParticipatedFieldMappingEntry';

  constructor(
    doc: PartialEntityData<FieldMappingEntry>,
    public override deserialize = (
      value: string | undefined,
      data: Record<string, string>,
      i: number,
      entityInitializer: PartialEntityData<Participation>,
    ): OptionalPromise<boolean | null> => /ja/i.test(value?.trim() ?? ''),
  ) {
    super(doc, deserialize);
  }
}
